/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { Table, Avatar, Typography, Space, Dropdown, Button } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import styled from "styled-components";
import type { ColumnsType, TableProps } from "antd/es/table";
import { IUser, IUserAdmin, IUserStatus } from "utils/types";
import { dateFromNow } from "utils";
import { useModalStore } from "store";

const { Text } = Typography;

export interface UserData {
  key: string;
  name: string;
  email: string;
  profession: string;
  premium: boolean;
  status: "active" | "suspended";
  activity: string;
  avatar: string;
}

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;

  .ant-table {
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    min-width: 650px;
  }

  .ant-table-thead > tr > th {
    background: #f8f9fa;
    font-size: 14px;
    font-family: "Manrope-Medium", sans-serif;
    color: #333;
    padding: 16px;
    white-space: nowrap;
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    font-family: "Manrope", sans-serif;
  }

  .pagination-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
  }

  @media (max-width: 768px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 12px 8px;
    }

    .ant-pagination-item {
      min-width: 28px;
      height: 28px;
      line-height: 26px;
    }
  }
`;

const StatusBadge = styled.span<{ status: "active" | "suspended" }>`
  padding: 4px 8px;
  border-radius: 100px;
  font-size: 12px;
  font-family: "Manrope-Medium", sans-serif;
  background-color: ${({ status }) =>
    status === "active" ? "#E7F7E8" : "#FFF6E5"};
  color: ${({ status }) => (status === "active" ? "#1F9D55" : "#F59E0B")};
  white-space: nowrap;
`;

const EmailCell = styled.div`
  @media (max-width: 992px) {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export interface UsersTableProps {
  isAdmin: boolean;
  onViewUser: (user: IUser) => void;
  onChangeUserStatus: (user: IUser, status: IUserStatus) => Promise<void>;
  onDeleteUser: (user: IUser) => Promise<void>;
  users: IUser[];
  pagination: TableProps<IUser>["pagination"];
}
export const UsersTable: React.FC<UsersTableProps> = ({
  onViewUser, users, onChangeUserStatus, pagination, isAdmin,
  onDeleteUser
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 992);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 992);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const openModal = useModalStore(state => state.openModal)

  const allColumns: ColumnsType<IUser> = [
    {
      title: "#",
      dataIndex: "key",
      key: "index",
      width: 50,
    },
    {
      title: "Full name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space size={12}>
          <Avatar style={{ backgroundColor: "#5575F7" }}>
            {record.avatar}
          </Avatar>
          <Text
            style={{ cursor: "pointer" }}
            onClick={() => onViewUser(record)}
          >
            {record.lastName || ""} {record.firstName || ""}
          </Text>
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (value) => <EmailCell title={value}>{value}</EmailCell>,
    },
    ...(isAdmin ? [
      {
        title: "Role",
        dataIndex: "userAdmin",
        key: "role",
        render(value: IUserAdmin){
          return value.adminRole
        }
      }
    ] : [
      {
        title: "Profession",
        dataIndex: "profession",
        key: "profession",
        responsive: ["md"],
      },
      {
        title: "Premium",
        dataIndex: "onActivePremium",
        key: "onActivePremium",
        responsive: ["lg"],
        render: (value) => (value === "true" ? "Yes" : "No"),
      },
    ] as  ColumnsType<IUser>),
    {
      title: "User status",
      dataIndex: "userStatus",
      key: "userStatus",
      render: (status) => (
        <StatusBadge status={status}>{status}</StatusBadge>
      ),
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      responsive: ["lg"],
      render(value) {
        return dateFromNow(value)
      },
    },
    {
      title: "Activity log",
      dataIndex: "lastOnline",
      key: "lastOnline",
      responsive: ["lg"],
      render(value) {
        return value ? dateFromNow(value) : ""
      },
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_, record) => (
        <Dropdown
          // overlay={getActionsMenu(record.id)}
          trigger={["click"]}
          placement="bottomRight"
          menu={{
            items: [
              ...(isAdmin ? [] : [{ key: "view", label: "View Details", onClick: () => onViewUser(record) }]),
              ...(record.userStatus === "active" ? [
                { key: "suspend", label: "Suspend User", danger: true , onClick: () => {
                  openModal(
                    "Suspend User",
                    "Suspending this user would make restrict his/her access, do you want to continue?",
                    async () => onChangeUserStatus(record, "suspended"),
                    false,
                  )
                }}
              ] : [
                { key: "activate", label: "Activate User", onClick: () => {
                  openModal(
                    "Activate User",
                    "Activating this user would give him access to the platform, do you want to continue?",
                    async () => onChangeUserStatus(record, "active"),
                    false,
                  )
                }},
              ]),
              { key: "delete", label: "Delete user", danger: true, onClick: () => {
                openModal(
                    "Delete User",
                    "Deleting this user would remove his/her access to the platform and course records, do you want to continue?",
                    async () => onDeleteUser(record),
                    true,
                  )
              }},
            ]
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <TableWrapper>
      <Table<IUser>
        dataSource={users}
        columns={allColumns}
        rowKey="key"
        scroll={{ x: "max-content" }}
        pagination={pagination}
      />
    </TableWrapper>
  );
};

export default UsersTable;
